// ScoutTeamProfilePage.jsx
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { CoachTeamHeader } from "./CoachTeamHeader";
import { RecentTeamStats } from "./RecentTeamStats";
import { PlayerTable } from "./PlayerTable";
import { SimpleDropdown } from '../../../components/common/SimpleDropdown';
import { fetchTeamRoster, loadMoreTeamRoster, fetchScoutGames } from '../../../features/scout/scoutProfileSlice';
import { ScoutSidebar } from '../dashboard/ScoutSidebar';
import { useOutletContext } from 'react-router-dom';
import { UpcomingGamesCarousel } from './DreamTeam/components/UpcomingGamesCarousel';
import { currentSeasonYear } from '../../../utils/season';

export const ScoutTeamProfilePage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { activeView } = useOutletContext();
  const { teamRoster, teamRosterPagination, teamRosterLoading, teamRosterError, rosterTeamInfo, scoutGames, scoutGamesLoading, scoutGamesError } = useSelector((state) => state.scoutProfile);

  // Filter state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterRanges, setFilterRanges] = useState({});
  const [rosterLimit, setRosterLimit] = useState(20);
  const [selectedStatColumns, setSelectedStatColumns] = useState([]);
  const [filterActiveTab, setFilterActiveTab] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [scope, setScope] = useState('overall');
  const [category, setCategory] = useState('');

  // Generate year list - current season and 10 previous years in season format (2026-27)
  const currentYear = currentSeasonYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => {
    const year = currentYear - i;
    return {
      label: `${year}-${(year + 1).toString().slice(-2)}`,
      value: year
    };
  });
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Get team data from navigation state or first player's team
  const teamFromState = location.state?.teamData;
  const currentTeamData = rosterTeamInfo || teamFromState || teamRoster[0]?.team || {};

  const handleApplyFilters = (filters) => {
    // Store selected filters for dynamic column display
    if (filters.selectedFilters && filters.selectedFilters.length > 0) {
      setSelectedStatColumns(filters.selectedFilters);
      setFilterActiveTab(filters.activeTab);
    } else {
      setSelectedStatColumns([]);
      setFilterActiveTab(null);
    }

    // Store filter ranges for API filtering (triggers refetch via useEffect)
    if (filters.filterRanges) {
      setFilterRanges(filters.filterRanges);
    } else {
      setFilterRanges({});
    }
  };

  const resolveStatsType = (tab, cat) => {
    if (tab === 'pitching' || cat === 'pitching') return 'pitching';
    if (tab === 'hitting' || cat === 'hitting') return 'batting';
    return null;
  };

  // Fetch team roster using team ID from route
  useEffect(() => {
    if (teamId) {
      const params = {
        teamId,
        page: 1,
        limit: rosterLimit,
        seasonYear: selectedYear,
        statsType: resolveStatsType(filterActiveTab, category),
        scope,
        category,
      };

      // Add filter ranges for API filtering
      if (Object.keys(filterRanges).length > 0) {
        params.filters = filterRanges;
      }

      // Add sort params
      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }

      dispatch(fetchTeamRoster(params));
    }
  }, [dispatch, teamId, selectedYear, filterRanges, rosterLimit, sortBy, sortOrder, scope, category, filterActiveTab]);

  // Fetch scout games
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    dispatch(fetchScoutGames({
      page: 1,
      limit: 10,
      startDate: today
    }));
  }, [dispatch]);

  const handleLoadMore = () => {
    if (teamId && teamRosterPagination?.hasMore && !teamRosterLoading) {
      const nextPage = (teamRosterPagination.currentPage || 1) + 1;
      const params = {
        teamId,
        page: nextPage,
        limit: rosterLimit,
        seasonYear: selectedYear,
        statsType: resolveStatsType(filterActiveTab, category),
        scope,
        category,
      };

      // Add filter ranges for API filtering
      if (Object.keys(filterRanges).length > 0) {
        params.filters = filterRanges;
      }

      // Add sort params
      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }

      dispatch(loadMoreTeamRoster(params));
    }
  };

  const handleSort = (backendField, direction) => {
    setSortBy(backendField);
    setSortOrder(direction);
  };

  const handleYearChange = (year) => {
    setSelectedYear(parseInt(year));
  };

  // Client-side filtering by category (backend doesn't filter by position/category)
  const filteredRoster = useMemo(() => {
    if (!category) return teamRoster;
    const isPitcher = (p) => {
      const pos = (p.primaryPosition || p.position || '').toUpperCase();
      return pos === 'RHP' || pos === 'LHP' || pos === 'P' || pos === 'PITCHER';
    };
    if (category === 'pitching') return teamRoster.filter(isPitcher);
    if (category === 'hitting') return teamRoster.filter((p) => !isPitcher(p));
    return teamRoster;
  }, [teamRoster, category]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Left Sidebar */}
      <div className="lg:col-span-1 border border-slate-700/50 rounded-2xl">
        <ScoutSidebar activeView={activeView || "teams"} />
      </div>

      {/* Right Content Area */}
      <main className="lg:col-span-3 flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-300 hover:text-white text-sm font-medium transition-colors mb-6"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to Teams</span>
          </button>

          {/* Loading State */}
          {teamRosterLoading && teamRoster.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Error State */}
          {teamRosterError && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
              {teamRosterError}
            </div>
          )}

          {/* Team Content - Show when we have team data from state or API */}
          {(teamFromState || currentTeamData._id || !teamRosterLoading) && (
            <div className="space-y-6">
              <div className="border border-slate-700/50 rounded-2xl p-4 sm:p-6 md:p-8">
                {/* Show header if we have team data from navigation state */}
                {(teamFromState || currentTeamData._id) && (
                  <CoachTeamHeader
                    selectedCollege={currentTeamData._id ? currentTeamData : teamFromState}
                    colleges={[currentTeamData._id ? currentTeamData : teamFromState]}
                    onSelectCollege={() => {}}
                  />
                )}

                {/* Year Selector */}
                <div className="flex items-center justify-end py-4 border-b border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-white">Season Year:</span>
                    <div style={{ minWidth: '140px' }}>
                      <SimpleDropdown
                        value={selectedYear}
                        onChange={handleYearChange}
                        options={yearOptions}
                        placeholder="Select Year"
                        variant="form"
                      />
                    </div>
                  </div>
                </div>

                {/* Upcoming Games Carousel */}
                {/* {scoutGames.length > 0 && (
                  <div className="mb-6">
                    <UpcomingGamesCarousel games={scoutGames.map(game => {
                      const isLive = game.status === 'live';

                      // Calculate time remaining for upcoming games
                      let timeInfo = '';
                      if (!isLive && game.date && game.time) {
                        const gameDateTime = new Date(`${game.date.split('T')[0]}T${game.time}:00`);
                        const now = new Date();
                        const timeDiff = gameDateTime - now;

                        if (timeDiff > 0) {
                          const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                          const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                          const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

                          if (days > 0) {
                            timeInfo = `${days}d ${hours}h`;
                          } else if (hours > 0) {
                            timeInfo = `${hours}h ${minutes}m`;
                          } else {
                            timeInfo = `${minutes}m`;
                          }
                        }
                      }

                      return {
                        id: game._id,
                        homeLogo: game.homeTeamId?.logo,
                        awayLogo: game.awayTeamId?.logo,
                        home: game.homeTeamId?.name || 'TBD',
                        away: game.awayTeamId?.name || 'TBD',
                        location: game.location || 'TBD',
                        date: new Date(game.date).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'long'
                        }),
                        time: game.time || '',
                        isLive,
                        timeRemaining: timeInfo,
                        streamLink: game.streamLink
                      };
                    })} />
                  </div>
                )} */}

                {(teamFromState || currentTeamData._id) && (
                  <RecentTeamStats
                    stats={currentTeamData._id ? currentTeamData : teamFromState}
                    showFilterModal={showFilterModal}
                    onFiltersClick={() => setShowFilterModal(!showFilterModal)}
                    onCloseModal={() => setShowFilterModal(false)}
                    onApplyFilters={handleApplyFilters}
                    statsType={scope}
                    onStatsTypeChange={setScope}
                    category={category}
                    onCategoryChange={setCategory}
                  />
                )}

                {/* Show player table or empty state */}
                {filteredRoster.length > 0 ? (
                  <PlayerTable
                    data={filteredRoster}
                    loading={teamRosterLoading}
                    pagination={teamRosterPagination}
                    onLoadMore={handleLoadMore}
                    limit={rosterLimit}
                    onLimitChange={(val) => setRosterLimit(val)}
                    selectedStatColumns={selectedStatColumns}
                    filterActiveTab={filterActiveTab}
                    sortColumn={sortBy}
                    sortDirection={sortOrder}
                    onSort={handleSort}
                  />
                ) : !teamRosterLoading && (
                  <div className="text-center py-12 text-gray-400">
                    <svg
                      className="w-16 h-16 mx-auto mb-4 text-slate-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    <p className="text-lg font-medium">No players found</p>
                    <p className="text-sm mt-2">No player data available for the {selectedYear}-{(selectedYear + 1).toString().slice(-2)} season.</p>
                    <p className="text-sm mt-1 text-slate-500">Try selecting a different season year.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ScoutTeamProfilePage;
